import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Loader2 } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { ConsentDialog } from './ConsentDialog';
import { ConsentCheckbox } from './ConsentCheckbox';
import { ConsentVersionStaleError, recordConsent } from '../api';
import type { ConsentSource, ConsentState } from '../types';

interface TermsStageProps {
  state: ConsentState;
  source: ConsentSource;
  onCompleted: () => void;
  onDecline: () => void;
}

/**
 * Stufe 1: Nutzungsbedingungen und Datenschutzerklaerung.
 *
 * Blockierend - ohne diese Grundlage gibt es keine Nutzung. Getrennte
 * Checkboxen und getrennte Ereignisse: aendert sich spaeter nur einer der
 * beiden Texte, entwertet das nicht auch die andere Zustimmung.
 *
 * Die Links fuehren auf /legal/terms und /legal/privacy. Beide Pfade stehen
 * in der Ausnahmeliste (consentPaths.ts), das Gate tritt dort also zur
 * Seite - sonst verdeckte es die Texte, zu denen es Zustimmung verlangt.
 */
export function TermsStage({ state, source, onCompleted, onDecline }: TermsStageProps) {
  const { t } = useTranslation(['consent', 'common']);
  const navigate = useNavigate();

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Nur abfragen, was tatsaechlich offen ist. Liegt die Zustimmung zu den
  // Nutzungsbedingungen vor und nur die Datenschutzerklaerung ist neu, waere
  // eine zweite Checkbox fuer die Nutzungsbedingungen sinnlose Arbeit.
  const needsTerms = state.requires_terms;
  const needsPrivacy = state.requires_privacy;

  const canSubmit =
    (!needsTerms || acceptedTerms) && (!needsPrivacy || acceptedPrivacy) && !isSaving;

  const handleAccept = async () => {
    if (!canSubmit) return;
    setIsSaving(true);
    setError(null);

    try {
      if (needsTerms) {
        await recordConsent({
          consentType: 'terms',
          decision: 'granted',
          version: state.versions.terms,
          source,
        });
      }
      if (needsPrivacy) {
        await recordConsent({
          consentType: 'privacy',
          decision: 'granted',
          version: state.versions.privacy,
          source,
        });
      }
      onCompleted();
    } catch (err) {
      console.error('Recording terms consent failed:', err);
      setError(
        err instanceof ConsentVersionStaleError
          ? t('consent:gate.versionChanged')
          : t('consent:gate.genericError')
      );
      setIsSaving(false);
    }
  };

  return (
    <ConsentDialog
      icon={<FileText className="w-5 h-5 text-blue-400" />}
      title={t('consent:terms.title')}
      footer={
        <>
          <button
            onClick={handleAccept}
            disabled={!canSubmit}
            className="w-full py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('consent:gate.saving')}
              </>
            ) : (
              t('consent:terms.accept')
            )}
          </button>
          <button
            onClick={onDecline}
            disabled={isSaving}
            className="w-full py-2.5 rounded-xl bg-white/10 border border-white/20 text-white/70 font-medium hover:bg-white/20 disabled:opacity-50"
          >
            {t('consent:terms.decline')}
          </button>
        </>
      }
    >
      <p className="text-white/80 text-sm mb-3">{t('consent:terms.intro')}</p>
      <p className="text-white/50 text-xs mb-4">{t('consent:terms.whyNow')}</p>

      <div className="space-y-3">
        {needsTerms && (
          <ConsentCheckbox
            checked={acceptedTerms}
            onChange={setAcceptedTerms}
            disabled={isSaving}
            label={t('consent:terms.termsCheckbox')}
            versionLabel={t('consent:terms.version', { version: state.versions.terms })}
            linkLabel={t('consent:terms.openTerms')}
            onLinkClick={() => navigate('/legal/terms')}
          />
        )}

        {needsPrivacy && (
          <ConsentCheckbox
            checked={acceptedPrivacy}
            onChange={setAcceptedPrivacy}
            disabled={isSaving}
            label={t('consent:terms.privacyCheckbox')}
            versionLabel={t('consent:terms.version', { version: state.versions.privacy })}
            linkLabel={t('consent:terms.openPrivacy')}
            onLinkClick={() => navigate('/legal/privacy')}
          />
        )}
      </div>

      <p className="text-white/40 text-xs mt-4">{t('consent:terms.declineHint')}</p>

      {error && (
        <div className="bg-red-500/20 text-red-300 text-sm p-3 rounded-[10px] mt-4">{error}</div>
      )}
    </ConsentDialog>
  );
}
